/**
 * 소리.
 *
 * 뱀파이어 서바이버즈류는 초당 수백 번 무언가가 맞고 죽는다.
 * 소리를 그대로 다 내면 5분만 지나도 귀가 아파서 음소거하게 된다.
 * 그래서 "무엇을 안 들려줄 것인가"를 먼저 정했다.
 *
 *   1. 무기는 **나갈 때 한 번, 맞을 때 한 번**. 광역기 한 방에 40마리가 맞아도 맞는 소리는 하나다.
 *   2. 종류마다 최소 간격이 있다. 그 안에 또 오면 그냥 버린다.
 *   3. 최근 1초에 소리가 많이 났으면 **그만큼 작게** 낸다.
 *      화면이 아수라장일수록 소리는 오히려 조용해진다.
 *   4. 효과음 버스에 리미터를 물려서 겹쳐도 찢어지지 않는다.
 *   5. 짧다(대부분 40~300ms). 길면 겹쳐서 웅웅거린다.
 *
 * 효과음은 거의 전부 코드로 합성한다(도트 이펙트와 같은 방식).
 * 예외는 둘: 복마검 소리(sfx_sword.wav)와 배경음(bgm_main.mp3). public/audio 에 있다.
 */

export type Sfx =
  // ── 나갈 때 — 무기마다 다르다 ──
  | 'sword' | 'greatsword' | 'ashSwing' | 'palm' | 'frostBlade' | 'whip' | 'windcut' | 'hammer' | 'thrust' | 'dash' | 'whirl'
  | 'flame' | 'iceShot' | 'zap' | 'thunder' | 'pop' | 'stoneThrow' | 'fireball' | 'splash' | 'jet'
  | 'quake' | 'chime' | 'royal' | 'gasBurst' | 'windBlast' | 'breeze' | 'steel' | 'arc' | 'static'
  | 'frostField' | 'fog' | 'pillar' | 'snowflake' | 'sigil' | 'pounce'
  | 'blizzard'    // 풍화설영 — 크게 가끔 몰아치는 눈보라
  | 'emberBurst'  // 플레임 스로우가 맞은 자리의 작은 폭발
  // ── 1.11 고유 스킬·반응·엘리트 ──
  | 'reflect'     // 기사의 검이 적 탄을 되받아쳤다 "팅"
  | 'shatter'     // 얼음이 산산조각 (쇄화·쇄빙)
  | 'steam'       // 증기 폭발 "치익"
  | 'beam'        // 메자이 황금 광선
  | 'cellBurst'   // 세포 역설 보호막이 깨지며 터진다
  | 'fusion'      // 조합하는 순간
  | 'chest'       // 보물 상자를 열었다
  | 'elite'       // 엘리트가 나타났다
  // ── 1.16 정령왕 등장 (받은 "정령 소환 소리"를 셋으로 — tools/build_audio.py build_summon) ──
  | 'summonRise'  // 고위 정령들이 모여들 때 — 원본을 거꾸로, 번쩍 직전에 빨려 들며 끊긴다
  | 'summonBlink' // 화면이 깜빡일 때 "칭"
  | 'summon'      // 번쩍! 정령왕 셋이 나타난다 — 원본 + 낮게 늘인 울림
  // ── 맞을 때 ──
  | 'hitSlash' | 'hitFire' | 'hitIce' | 'hitShock' | 'hitWind' | 'hitRock' | 'hitWater' | 'hitMagic'
  | 'grind' | 'boomFire' | 'boomAir' | 'boom'
  // ── 기사: 거대한 검 ──
  | 'clash'       // 검이 나타날 때 (받은 음원)
  | 'bladeSlash'  // 스윽 / 슥
  | 'bladeSpin'   // 휭
  // ── 그 밖 ──
  | 'kill'    // 적이 죽었다 (뭉쳐서 낸다)
  | 'gem'     // 경험치를 주웠다
  | 'levelup'
  | 'hurt'    // 내가 맞았다
  | 'freeze'  // 적이 얼었다
  | 'boss'
  | 'dead'
  | 'clear'
  | 'ui';

interface SfxConfig {
  /** 이 종류의 기본 음량 */
  gain: number;
  /** 최소 간격(초). 이 안에 또 요청되면 버린다. */
  minGap: number;
  /** 소리가 몰릴 때 음량이 줄어드는 정도. 0이면 안 줄어든다(중요한 소리). */
  duck: number;
  /** 대략의 길이(초). 동시 발음 수를 세는 데만 쓴다. */
  dur: number;
  /** 같은 무리끼리 공유하는 최소 간격(GROUP_GAP). 맞는 소리는 전부 한 무리다. */
  group?: 'hit';
}

const CONFIG: Record<Sfx, SfxConfig> = {
  // ── 나갈 때: 자주 난다. 몰리면 줄인다 ──
  // 음량(gain)은 소리마다 실제로 렌더링해 체감 음량을 맞춘 값이다(검 -18dB, 무거운 기술 -23, 보통 -26,
  // 자주 나는 것 -29, 맞는 소리 -32). 숫자가 1을 넘는 건 필터로 깎이는 잡음 소리라서 그렇다.
  // 복마검은 주인공의 기본기라 덜 줄인다(duck 0.5). 두 번 벨 때 0.14초 간격이 살아 있도록 minGap 을 짧게.
  sword: { gain: 0.247, minGap: 0.08, duck: 0.5, dur: 0.75 },
  greatsword: { gain: 0.239, minGap: 0.09, duck: 0.6, dur: 0.9 },
  // 회광반조 잿빛대검 "휘-익" + 불티 (받은 음원 세 가지를 번갈아)
  // 소한공 — 손바람 "훅" + 얼음 방울 (귀로 안 맞췄다)
  palm: { gain: 0.3, minGap: 0.08, duck: 0.8, dur: 0.2 },
  ashSwing: { gain: 0.37, minGap: 0.1, duck: 0.5, dur: 0.9 }, // 파일 체감 음량이 검 음원보다 3.7dB 작아서 예전 회광반조(검 음원 × 0.239)와 같게
  frostBlade: { gain: 0.3, minGap: 0.12, duck: 1, dur: 0.22 },
  whip: { gain: 0.46, minGap: 0.12, duck: 1, dur: 0.16 },
  windcut: { gain: 0.998, minGap: 0.2, duck: 1, dur: 0.07 },
  hammer: { gain: 0.836, minGap: 0.15, duck: 1, dur: 0.24 },
  thrust: { gain: 0.391, minGap: 0.12, duck: 1, dur: 0.22 },
  dash: { gain: 0.36, minGap: 0.16, duck: 1, dur: 0.1 },
  whirl: { gain: 0.813, minGap: 0.18, duck: 1, dur: 0.24 },
  flame: { gain: 0.332, minGap: 0.12, duck: 1, dur: 0.18 },
  iceShot: { gain: 0.258, minGap: 0.12, duck: 1, dur: 0.14 },
  zap: { gain: 0.205, minGap: 0.09, duck: 1, dur: 0.07 },
  thunder: { gain: 0.196, minGap: 0.3, duck: 0.6, dur: 2.15 },
  pop: { gain: 0.221, minGap: 0.12, duck: 1, dur: 0.1 },
  stoneThrow: { gain: 0.577, minGap: 0.12, duck: 1, dur: 0.08 },
  fireball: { gain: 0.318, minGap: 0.2, duck: 0.8, dur: 0.32 },
  splash: { gain: 0.534, minGap: 0.15, duck: 1, dur: 0.2 },
  jet: { gain: 0.116, minGap: 0.14, duck: 1, dur: 0.24 },
  quake: { gain: 0.178, minGap: 0.2, duck: 0.8, dur: 1.27 },
  chime: { gain: 0.107, minGap: 0.45, duck: 1.2, dur: 0.13 },
  royal: { gain: 0.21, minGap: 0.4, duck: 0.4, dur: 3.0 },
  gasBurst: { gain: 0.125, minGap: 0.2, duck: 1, dur: 0.83 },
  windBlast: { gain: 1.048, minGap: 0.2, duck: 1, dur: 0.3 },
  breeze: { gain: 0.561, minGap: 0.2, duck: 1.2, dur: 0.18 },
  steel: { gain: 0.148, minGap: 0.2, duck: 0.8, dur: 0.3 },
  arc: { gain: 0.235, minGap: 0.12, duck: 1, dur: 0.7 },
  static: { gain: 0.354, minGap: 0.1, duck: 1, dur: 0.12 },
  frostField: { gain: 0.388, minGap: 0.3, duck: 1, dur: 0.32 },
  fog: { gain: 0.423, minGap: 0.4, duck: 1, dur: 0.42 },
  pillar: { gain: 0.299, minGap: 0.3, duck: 1, dur: 0.3 },
  snowflake: { gain: 0.158, minGap: 0.25, duck: 1, dur: 0.2 },
  sigil: { gain: 0.115, minGap: 1.0, duck: 0.5, dur: 0.9 },
  pounce: { gain: 1.135, minGap: 0.3, duck: 0.6, dur: 0.14 },
  // 1.10: 눈보라는 무거운 기술(-23), 작은 불 폭발은 자주 나는 것(-29)에 맞췄다
  blizzard: { gain: 0.38, minGap: 1.0, duck: 0.6, dur: 0.75 },
  emberBurst: { gain: 0.274, minGap: 0.09, duck: 1, dur: 0.12 },
  // 1.11: 귀로·측정으로 맞추지 않았다. 비슷한 급의 기존 소리 gain 에 맞춰 두었다
  reflect: { gain: 0.3, minGap: 0.1, duck: 0.8, dur: 0.2 },
  shatter: { gain: 0.26, minGap: 0.12, duck: 1, dur: 0.18 },
  steam: { gain: 0.22, minGap: 0.35, duck: 1, dur: 0.3 }, // 불+서리 조합이면 초당 3번 넘게 나서 간격을 넓혔다
  beam: { gain: 0.3, minGap: 0.4, duck: 0.5, dur: 0.45 },
  cellBurst: { gain: 0.34, minGap: 0.5, duck: 0.3, dur: 0.5 },
  // 1.16 정령왕 등장: 판에 한 번뿐인 큰 순간이라 줄이지 않는다(duck 0 — 동시 발음 수가 꽉 차도 난다).
  // 50ms RMS 최댓값: 번쩍 -19dB(검 -18 바로 아래) · 모여듦은 끝에서 -23.5 까지 차오름(번쩍과 대비가 나게) · 깜빡 -24. 귀로는 안 맞췄다.
  summonRise: { gain: 0.24, minGap: 1.0, duck: 0, dur: 1.18 },
  summonBlink: { gain: 0.257, minGap: 0.05, duck: 0, dur: 0.14 },
  summon: { gain: 0.447, minGap: 1.0, duck: 0, dur: 3.6 },
  // 1.9 에 파일로 바꾼 소리(thunder·royal·quake·gasBurst·arc)는 바꾸기 전 합성음과 같은 크기로 맞췄다.
  // quake 는 4레벨, arc 는 5번 옮겨붙을 때 기준. 기사: 검 등장(clash)은 검(-18), 스윽/슥은 무거운 기술, 휭은 보통.
  clash: { gain: 0.6, minGap: 0.3, duck: 0.4, dur: 0.82 },
  bladeSlash: { gain: 0.134, minGap: 0.12, duck: 0.5, dur: 1.14 },
  bladeSpin: { gain: 1.31, minGap: 0.12, duck: 0.6, dur: 0.25 },

  // ── 맞을 때: 짧게, 몰리면 확 줄인다 ──
  hitSlash: { gain: 0.198, minGap: 0.2, duck: 1.2, dur: 0.06, group: 'hit' },
  hitFire: { gain: 0.472, minGap: 0.14, duck: 1.2, dur: 0.08, group: 'hit' },
  hitIce: { gain: 0.243, minGap: 0.12, duck: 1.2, dur: 0.05, group: 'hit' },
  hitShock: { gain: 0.35, minGap: 0.12, duck: 1.2, dur: 0.04, group: 'hit' },
  hitWind: { gain: 0.71, minGap: 0.14, duck: 1.2, dur: 0.06, group: 'hit' },
  hitRock: { gain: 0.126, minGap: 0.14, duck: 1.2, dur: 0.08, group: 'hit' },
  hitWater: { gain: 0.289, minGap: 0.14, duck: 1.2, dur: 0.07, group: 'hit' },
  hitMagic: { gain: 0.148, minGap: 0.15, duck: 1.2, dur: 0.05, group: 'hit' },
  grind: { gain: 0.376, minGap: 0.5, duck: 1.4, dur: 0.05, group: 'hit' },
  boomFire: { gain: 0.215, minGap: 0.15, duck: 0.7, dur: 0.4 },
  boomAir: { gain: 0.209, minGap: 0.12, duck: 0.8, dur: 0.2 },
  boom: { gain: 0.262, minGap: 0.3, duck: 0.4, dur: 0.45 },

  // ── 잡음에 가까운 것들: 아주 작게, 아주 드물게 ──
  kill: { gain: 0.16, minGap: 0.3, duck: 1.4, dur: 0.06 },
  gem: { gain: 0.13, minGap: 0.07, duck: 1.2, dur: 0.07 },
  freeze: { gain: 0.26, minGap: 0.85, duck: 0.8, dur: 0.16 },

  // ── 알림: 드물게 나므로 또렷하게. 줄이지 않는다 ──
  hurt: { gain: 0.55, minGap: 0.25, duck: 0, dur: 0.22 },
  levelup: { gain: 0.5, minGap: 0.2, duck: 0, dur: 0.45 },
  boss: { gain: 0.6, minGap: 1.0, duck: 0, dur: 1.2 },
  dead: { gain: 0.6, minGap: 1.0, duck: 0, dur: 0.95 },
  clear: { gain: 0.6, minGap: 1.0, duck: 0, dur: 0.9 },
  fusion: { gain: 0.55, minGap: 1.0, duck: 0, dur: 1.3 },
  chest: { gain: 0.5, minGap: 0.5, duck: 0, dur: 0.8 },
  elite: { gain: 0.45, minGap: 1.0, duck: 0, dur: 0.9 },
  ui: { gain: 0.3, minGap: 0.04, duck: 0, dur: 0.06 },
};

/**
 * 무리별 최소 간격(초). 맞는 소리는 종류가 달라도 합쳐서 이 간격보다 촘촘하게 나지 않는다.
 * 무기 여섯 개가 동시에 적을 때려도 "맞았다"는 소리는 초당 10번을 넘지 않는다.
 */
const GROUP_GAP = { hit: 0.12 } as const;

/** 동시에 울릴 수 있는 소리 수. 넘으면 새 소리를 버린다. */
const MAX_VOICES = 20;
/** 몰림 판정 창(초) */
const DENSITY_WINDOW = 1.0;

/** 파일로 된 효과음 */
const SAMPLES = {
  sword: 'audio/sfx_sword.wav',
  clash: 'audio/sfx_clash.wav',
  fire: 'audio/sfx_fire.wav',
  quake: 'audio/sfx_quake.wav',
  thunder: 'audio/sfx_thunder.wav',
  royal: 'audio/sfx_royal.wav',
  elec1: 'audio/sfx_elec_1.wav',
  elec2: 'audio/sfx_elec_2.wav',
  elec3: 'audio/sfx_elec_3.wav',
  elec4: 'audio/sfx_elec_4.wav',
  ash1: 'audio/sfx_ash_1.wav',
  ash2: 'audio/sfx_ash_2.wav',
  ash3: 'audio/sfx_ash_3.wav',
  summonRise: 'audio/sfx_summon_rise.wav',
  summonBlink: 'audio/sfx_summon_blink.wav',
  summon: 'audio/sfx_summon.wav',
} as const;

/** 고음이 알맹이인 음원(전기·쇳소리)은 효과음 버스의 고음 깎기(9kHz)를 거치지 않는다 */
const BRIGHT: ReadonlySet<SampleName> = new Set<SampleName>([
  'clash', 'elec1', 'elec2', 'elec3', 'elec4',
  // 정령왕 등장 — 반짝임이 9kHz 위에 많다
  'summonRise', 'summonBlink', 'summon',
]);
type SampleName = keyof typeof SAMPLES;

/**
 * 배경음. 처음 한 번은 파일 맨 앞(도입부)부터 듣고, 그 뒤로는 loopStart~loopEnd 만 돈다.
 * 값은 tools/build_audio.py 가 계산해서 알려준다(135BPM, 88마디 = 156.44초).
 */
const MUSIC = {
  url: 'audio/bgm_main.mp3',
  loopStart: 13.121,
  loopEnd: 169.565438,
};

/**
 * 소리 모드.
 *  full  — 전부
 *  quiet — 알림만 (레벨업·피격·보스·사망). 15분쯤 하다 보면 전투음이 물릴 수 있어서 둔 중간 단계.
 *          배경음은 그대로 흐른다.
 *  off   — 끔 (배경음까지)
 */
export type SoundMode = 'full' | 'quiet' | 'off';

export interface PlayOpts {
  /** 재생 속도(=음높이) 배율 */
  rate?: number;
  /** 음량 배율 */
  gain?: number;
  /** 전류: 이번에 옮겨붙은 수. 많을수록 길고 큰 소리 */
  hops?: number;
  /** 무기 레벨 (소리 크기를 레벨에 맞출 때) */
  level?: number;
}

const STORAGE_KEY = 'salvatris.audio';

export class Audio {
  ctx: AudioContext | null = null;
  private master!: GainNode;
  private sfxBus!: BiquadFilterNode;
  /** 고음을 깎지 않는 효과음 입구 (리미터로 바로 간다) */
  private sfxBright!: DynamicsCompressorNode;
  private musicBus!: GainNode;
  private noiseBuf: AudioBuffer | null = null;

  private volume = 0.7;
  /** 배경음은 효과음보다 한 발 뒤에 깐다(기본 35%). 효과음이 묻히면 무기를 바꿔도 티가 안 난다. */
  private musicVolume = 0.35;
  private mode: SoundMode = 'full';

  private lastPlay = new Map<Sfx, number>();
  private lastGroup = new Map<string, number>();
  /** 최근에 소리가 난 시각들 (몰림 판정용) */
  private recent: number[] = [];
  /**
   * 울리고 있는 소리들이 끝나는 시각.
   * 예전에는 onended 콜백으로 셌는데, 콜백이 한 번이라도 안 오면
   * 카운터가 새서 소리가 영영 안 나게 된다. 시각으로 세면 그럴 일이 없다.
   */
  private voiceEnds: number[] = [];

  /** 보석 소리는 주울수록 반음씩 올라간다. 잠깐 쉬면 도로 내려온다. */
  private gemStep = 0;
  private gemLast = 0;

  // 파일 음원
  private readonly raw = new Map<string, Promise<ArrayBuffer | null>>();
  private readonly buffers = new Map<string, AudioBuffer>();

  // 배경음
  private musicSrc: AudioBufferSourceNode | null = null;
  private musicFade: GainNode | null = null;
  /** 판이 돌고 있어서 배경음이 나와야 하는가 (디코딩이 늦게 끝나도 틀어주려고) */
  private musicWanted = false;
  private musicDucked = false;

  /** 개발용: 종류별 [요청, 실제 재생] 수 */
  readonly stats = new Map<Sfx, [number, number]>();

  constructor() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const v = JSON.parse(raw) as { volume?: number; music?: number; mode?: SoundMode };
        if (typeof v.volume === 'number') this.volume = Math.min(1, Math.max(0, v.volume));
        if (typeof v.music === 'number') this.musicVolume = Math.min(1, Math.max(0, v.music));
        if (v.mode === 'full' || v.mode === 'quiet' || v.mode === 'off') this.mode = v.mode;
      }
    } catch {
      /* 저장값이 없거나 못 읽으면 기본값으로 간다 */
    }
  }

  // ── 준비 ────────────────────────────────────────────────

  /**
   * 음원 파일을 미리 받아 둔다(디코딩은 소리를 열 때). 부팅 때 한 번 부른다.
   * 실패해도 게임은 돈다 — 검 소리는 합성음으로, 배경음은 없이.
   */
  preload(): void {
    const get = (url: string): Promise<ArrayBuffer | null> =>
      fetch(url)
        .then((r) => (r.ok ? r.arrayBuffer() : null))
        .catch(() => null);
    for (const [name, url] of Object.entries(SAMPLES)) this.raw.set(name, get(url));
    this.raw.set('music', get(MUSIC.url));
  }

  /** 브라우저는 사용자가 뭔가 누르기 전에는 소리를 못 내게 한다. 첫 입력에 부른다. */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();

    // 효과음: 살짝 로우패스(고음 피로) → 리미터(겹쳐도 안 찢어지게)
    const soften = ctx.createBiquadFilter();
    soften.type = 'lowpass';
    soften.frequency.value = 9000;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -14;
    limiter.knee.value = 12;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.18;
    soften.connect(limiter);

    // 배경음은 리미터를 거치지 않는다(효과음이 몰릴 때 음악이 같이 눌려 울렁거리지 않게)
    const musicBus = ctx.createGain();
    musicBus.gain.value = this.musicVolume;

    // 마스터 → 마지막 안전장치(합쳐서 0dB 를 넘지 않게) → 출력
    const master = ctx.createGain();
    master.gain.value = this.mode === 'off' ? 0 : this.volume;
    const safety = ctx.createDynamicsCompressor();
    safety.threshold.value = -2;
    safety.knee.value = 3;
    safety.ratio.value = 16;
    safety.attack.value = 0.002;
    safety.release.value = 0.12;

    limiter.connect(master);
    musicBus.connect(master);
    master.connect(safety);
    safety.connect(ctx.destination);

    // 잡음 원본은 한 번만 만들고 매번 다른 지점부터 잘라 쓴다
    const n = ctx.sampleRate;
    const nb = ctx.createBuffer(1, n, ctx.sampleRate);
    const d = nb.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;

    this.ctx = ctx;
    this.master = master;
    this.sfxBus = soften;
    this.sfxBright = limiter;
    this.musicBus = musicBus;
    this.noiseBuf = nb;

    void this.decodeAll();
  }

  private async decodeAll(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    for (const [name, p] of this.raw) {
      const data = await p;
      if (!data) {
        console.warn(`[audio] 음원을 못 받았다: ${name}`);
        continue;
      }
      try {
        const buf = await new Promise<AudioBuffer>((res, rej) => {
          const r = ctx.decodeAudioData(data, res, rej) as Promise<AudioBuffer> | undefined;
          r?.catch(() => {});
        });
        this.buffers.set(name, buf);
        if (name === 'music' && this.musicWanted && !this.musicSrc) this.startMusicNow();
      } catch (e) {
        console.warn(`[audio] 음원을 못 풀었다: ${name}`, e);
      }
    }
  }

  get soundMode(): SoundMode {
    return this.mode;
  }

  get isMuted(): boolean {
    return this.mode === 'off';
  }

  get level(): number {
    return this.volume;
  }

  get musicLevel(): number {
    return this.musicVolume;
  }

  setVolume(v: number): void {
    this.volume = Math.min(1, Math.max(0, v));
    if (this.mode === 'off' && this.volume > 0) this.mode = 'full';
    this.apply();
  }

  setMusicVolume(v: number): void {
    this.musicVolume = Math.min(1, Math.max(0, v));
    this.apply();
  }

  setMode(mode: SoundMode): void {
    this.mode = mode;
    this.apply();
  }

  /** 전부 → 알림만 → 끔 → 전부 ... 로 한 칸씩 돈다. */
  cycleMode(): SoundMode {
    this.mode = this.mode === 'full' ? 'quiet' : this.mode === 'quiet' ? 'off' : 'full';
    this.apply();
    return this.mode;
  }

  private apply(): void {
    if (this.ctx) {
      const t = this.ctx.currentTime;
      this.master.gain.setTargetAtTime(this.mode === 'off' ? 0 : this.volume, t, 0.02);
      this.musicBus.gain.setTargetAtTime(this.musicVolume * (this.musicDucked ? 0.35 : 1), t, 0.05);
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ volume: this.volume, music: this.musicVolume, mode: this.mode }));
    } catch {
      /* 저장 못 해도 이번 판은 그대로 쓴다 */
    }
  }

  // ── 배경음 ──────────────────────────────────────────────

  /** 판이 시작될 때 부른다. 늘 처음(도입부)부터 다시 튼다. */
  startMusic(): void {
    this.musicWanted = true;
    this.musicDucked = false;
    this.fadeOutMusic(0.25);
    if (this.buffers.has('music')) this.startMusicNow();
  }

  /** 죽거나 나갈 때. */
  stopMusic(fadeSec = 1.5): void {
    this.musicWanted = false;
    this.fadeOutMusic(fadeSec);
  }

  /** 일시정지 동안은 작게 깐다. */
  setMusicDucked(ducked: boolean): void {
    this.musicDucked = ducked;
    this.apply();
  }

  private startMusicNow(): void {
    const ctx = this.ctx;
    const buf = this.buffers.get('music');
    if (!ctx || !buf) return;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    // 디코더가 앞에 여유 샘플을 붙이는 브라우저가 있어도 파일 쪽 이음매가 0.25초 폭으로 겹쳐 있어 티가 안 난다
    src.loopStart = Math.min(MUSIC.loopStart, buf.duration - 1);
    src.loopEnd = Math.min(MUSIC.loopEnd, buf.duration);
    const fade = ctx.createGain();
    const t = ctx.currentTime;
    fade.gain.setValueAtTime(0.0001, t);
    fade.gain.exponentialRampToValueAtTime(1, t + 0.08);
    src.connect(fade);
    fade.connect(this.musicBus);
    src.start(t);
    this.musicSrc = src;
    this.musicFade = fade;
  }

  private fadeOutMusic(sec: number): void {
    const ctx = this.ctx;
    const src = this.musicSrc;
    const fade = this.musicFade;
    this.musicSrc = null;
    this.musicFade = null;
    if (!ctx || !src || !fade) return;
    const t = ctx.currentTime;
    fade.gain.cancelScheduledValues(t);
    fade.gain.setValueAtTime(Math.max(0.0001, fade.gain.value), t);
    fade.gain.exponentialRampToValueAtTime(0.0001, t + Math.max(0.02, sec));
    try {
      src.stop(t + sec + 0.05);
    } catch {
      /* 이미 멈췄다 */
    }
  }

  /** 최소 간격·몰림 기록을 비운다. 개발 도구가 시계를 건드린 뒤에 부른다. */
  resetThrottles(): void {
    this.lastPlay.clear();
    this.lastGroup.clear();
    this.recent.length = 0;
    this.voiceEnds.length = 0;
  }

  // ── 재생 ────────────────────────────────────────────────

  play(kind: Sfx, opts?: PlayOpts): void {
    const st = this.stats.get(kind) ?? [0, 0];
    st[0]++;
    this.stats.set(kind, st);

    const ctx = this.ctx;
    if (!ctx || this.mode === 'off' || this.volume <= 0) return;
    if (ctx.state !== 'running') return;

    const cfg = CONFIG[kind];
    // '알림만' 모드에서는 전투 잡음(duck > 0 인 것들)을 통째로 뺀다
    if (this.mode === 'quiet' && cfg.duck > 0) return;
    const now = ctx.currentTime;

    // 같은 종류를 너무 빨리 또 내지 않는다
    const last = this.lastPlay.get(kind) ?? -1;
    if (now - last < cfg.minGap) return;
    if (cfg.group && now - (this.lastGroup.get(cfg.group) ?? -1) < GROUP_GAP[cfg.group]) return;

    // 끝난 소리를 지우고 동시 발음 수를 본다
    let live = 0;
    for (let i = 0; i < this.voiceEnds.length; i++) {
      if (this.voiceEnds[i] > now) this.voiceEnds[live++] = this.voiceEnds[i];
    }
    this.voiceEnds.length = live;
    // 알림(duck 0 — 레벨업·보스·조합·정령왕 등장)은 드물고 놓치면 안 되니 동시 발음 수가 꽉 차도 낸다
    if (live >= MAX_VOICES && cfg.duck > 0) return;

    this.lastPlay.set(kind, now);
    if (cfg.group) this.lastGroup.set(cfg.group, now);
    this.voiceEnds.push(now + cfg.dur);
    st[1]++;

    // 최근 1초에 몇 번 났는지 세어 그만큼 줄인다
    let keep = 0;
    for (let i = 0; i < this.recent.length; i++) {
      if (now - this.recent[i] <= DENSITY_WINDOW) this.recent[keep++] = this.recent[i];
    }
    this.recent.length = keep;
    const density = keep;
    this.recent.push(now);
    // 무기마다 나갈 때·맞을 때 소리가 있어서 예전보다 기본 개수가 많다(후반 초당 18~24회).
    // 그만큼 한 개당 줄이는 폭은 작게 — 많이 줄이면 배경음에 묻혀 무기 소리가 안 들린다.
    const duck = cfg.duck > 0 ? 1 / (1 + density * 0.06 * cfg.duck) : 1;

    const g = cfg.gain * duck * (opts?.gain ?? 1);
    // 같은 소리라도 매번 조금씩 다르게 (기계적으로 들리지 않게)
    const rate = opts?.rate ?? 1;
    const p = this.rand(0.93, 1.07) * rate;

    switch (kind) {
      // 나갈 때
      case 'sword': return this.sword(g, rate);
      case 'greatsword': return this.greatsword(g, rate);
      case 'ashSwing': return this.ashSwing(g, rate);
      case 'palm': return this.palm(g, p);
      case 'frostBlade': return this.frostBlade(g, p);
      case 'whip': return this.whip(g, p);
      case 'windcut': return this.noise(g, 0.06, 'bandpass', 3200 * p, 6800 * p, 4);
      case 'hammer': return this.noise(g, 0.22, 'lowpass', 900 * p, 180, 1.1);
      case 'thrust': return this.thrust(g, p);
      case 'dash': return this.dash(g, p);
      case 'whirl': return this.whirl(g, p);
      case 'flame': return this.flame(g, p);
      case 'iceShot': return this.iceShot(g, p);
      case 'zap': return this.zap(g, p);
      case 'thunder': return this.thunder(g, p);
      case 'clash': return void this.sample('clash', g, this.rand(0.97, 1.03));
      case 'bladeSlash': return this.bladeSlash(g, p);
      case 'bladeSpin': return this.bladeSpin(g, p);
      case 'pop': return this.pop(g, p);
      case 'stoneThrow': return this.stoneThrow(g, p);
      case 'fireball': return this.fireball(g, p);
      case 'splash': return this.splash(g, p);
      case 'jet': return this.noise(g, 0.22, 'highpass', 1800 * p, 5600 * p, 0.7, 0, 0.04);
      case 'quake': return this.quake(g, p, opts?.level ?? 1);
      case 'chime': return this.chime(g, p);
      case 'royal': return this.royal(g, p);
      case 'gasBurst': return this.gasBurst(g, p);
      case 'windBlast': return this.windBlast(g, p);
      case 'breeze': return this.noise(g, 0.16, 'bandpass', 800 * p, 1700 * p, 1, 0, 0.04);
      case 'steel': return this.steel(g, p);
      case 'arc': return this.arc(g, opts?.hops ?? 1, rate);
      case 'static': return this.staticZap(g, p);
      case 'frostField': return this.frostField(g, p);
      case 'fog': return this.noise(g, 0.4, 'lowpass', 900 * p, 450, 0.5, 0, 0.12);
      case 'pillar': return this.pillar(g, p);
      case 'snowflake': return this.snowflake(g, p);
      case 'sigil': return this.sigil(g, p);
      case 'pounce': return this.noise(g, 0.13, 'bandpass', 600 * p, 2600 * p, 1.2);
      case 'blizzard': return this.blizzard(g, p);
      case 'emberBurst': return this.emberBurst(g, p);
      case 'reflect': return this.metal(g, 1900 * p, 0.2);
      case 'shatter': return this.shatter(g, p);
      case 'steam': return this.noise(g, 0.3, 'highpass', 3000 * p, 7000, 0.7, 0, 0.02);
      case 'beam': return this.beam(g, p);
      case 'cellBurst': return this.cellBurst(g, p);
      case 'fusion': return this.fusion(g);
      case 'chest': return this.arp(g, [659.25, 783.99, 987.77, 1318.5, 1567.98], 0.06);
      case 'elite': return this.elite(g);
      // 정령왕 등장 — 받은 음원만 (못 받았으면 함께 나는 합성음·번개 소리로 버틴다).
      // 모여듦은 번쩍하는 순간에 맞춰 끝나야 해서 재생 속도를 흔들지 않는다
      case 'summonRise': return void this.sample('summonRise', g, 1);
      case 'summonBlink': return void this.sample('summonBlink', g, p);
      case 'summon': return void this.sample('summon', g, rate);
      // 맞을 때
      case 'hitSlash': return this.hitSlash(g, p);
      case 'hitFire': return this.hitFire(g, p);
      case 'hitIce': return this.hitIce(g, p);
      case 'hitShock': return this.hitShock(g, p);
      case 'hitWind': return this.noise(g, 0.05, 'bandpass', 900 * p, 480, 1);
      case 'hitRock': return this.hitRock(g, p);
      case 'hitWater': return this.hitWater(g, p);
      case 'hitMagic': return this.tone(g, 1500 * p, 0.04, 'sine', 1100 * p);
      case 'grind': return this.noise(g, 0.05, 'bandpass', 2600 * p, 1800, 2);
      case 'boomFire': return this.boomFire(g, p);
      case 'boomAir': return this.boomAir(g, p);
      case 'boom': return this.boom(g, p);
      // 그 밖
      case 'kill': return this.kill(g);
      case 'gem': return this.gem(g);
      case 'freeze': return this.freeze(g);
      case 'hurt': return this.hurt(g);
      case 'levelup': return this.arp(g, [523.25, 659.25, 783.99, 1046.5], 0.085);
      case 'boss': return this.boss(g);
      case 'dead': return this.dead(g);
      case 'clear': return this.arp(g, [392, 523.25, 659.25, 783.99, 1046.5], 0.13);
      case 'ui': return this.tone(g, 880, 0.05, 'square');
    }
  }

  // ── 음원 만들기: 기본 재료 ──────────────────────────────

  private env(node: GainNode, t: number, peak: number, attack: number, dur: number): void {
    node.gain.setValueAtTime(0.0001, t);
    node.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
    node.gain.exponentialRampToValueAtTime(0.0001, t + Math.max(attack + 0.005, dur));
  }

  /** 잡음 한 조각. 필터를 쓸어 "쉭/퍽/후우" 같은 소리를 만든다. */
  private noise(
    g: number, dur: number, type: BiquadFilterType,
    f0: number, f1: number, q: number, delay = 0, attack = 0.006,
  ): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime + delay;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const flt = ctx.createBiquadFilter();
    flt.type = type;
    flt.Q.value = q;
    flt.frequency.setValueAtTime(Math.min(20000, f0), t);
    flt.frequency.exponentialRampToValueAtTime(Math.min(20000, Math.max(40, f1)), t + dur);
    const amp = ctx.createGain();
    this.env(amp, t, g, attack, dur);
    src.connect(flt);
    flt.connect(amp);
    amp.connect(this.sfxBus);
    src.start(t, Math.random() * 0.45, dur + 0.03);
  }

  /** 음 하나. */
  private tone(
    g: number, freq: number, dur: number, type: OscillatorType,
    sweepTo?: number, delay = 0,
  ): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (sweepTo) osc.frequency.exponentialRampToValueAtTime(Math.max(20, sweepTo), t + dur);
    const amp = ctx.createGain();
    this.env(amp, t, g, 0.008, dur);
    osc.connect(amp);
    amp.connect(this.sfxBus);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  /** 잘게 튀는 잡음 알갱이들 (전기·불티·자갈) */
  private crackle(g: number, dur: number, count: number, fLo: number, fHi: number, delay = 0): void {
    for (let i = 0; i < count; i++) {
      this.noise(
        g * this.rand(0.5, 1), this.rand(0.006, 0.016), 'bandpass',
        this.rand(fLo, fHi), this.rand(fLo * 0.6, fHi * 0.6), 8, delay + this.rand(0, dur),
      );
    }
  }

  /** 쇳소리 — 배음이 정수배가 아닌 사인파 몇 겹 */
  private metal(g: number, f: number, dur: number, delay = 0): void {
    const parts: Array<[number, number, number]> = [[1, 1, 1], [2.76, 0.5, 0.7], [5.4, 0.3, 0.5], [8.93, 0.15, 0.35]];
    for (const [m, a, d] of parts) this.tone(g * a, f * m, dur * d, 'sine', undefined, delay);
  }

  /** 파일 음원. 아직 안 풀렸으면 false. */
  private sample(name: SampleName, g: number, rate: number, delay = 0): boolean {
    const buf = this.buffers.get(name);
    if (!buf) return false;
    const ctx = this.ctx!;
    const t = ctx.currentTime + delay;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    const amp = ctx.createGain();
    amp.gain.value = g;
    src.connect(amp);
    amp.connect(BRIGHT.has(name) ? this.sfxBright : this.sfxBus);
    src.start(t);
    return true;
  }

  private arp(g: number, notes: number[], step: number): void {
    notes.forEach((n, i) => {
      this.tone(g * (i === notes.length - 1 ? 1 : 0.75), n, step * 1.8, 'triangle', undefined, i * step);
    });
  }

  private rand(a: number, b: number): number {
    return a + Math.random() * (b - a);
  }

  // ── 나갈 때 ────────────────────────────────────────────

  // 복마검 — 진짜 검 소리(파일). 못 받았으면 합성 "휙"으로 대신한다.
  private sword(g: number, rate: number): void {
    if (this.sample('sword', g, rate)) return;
    const p = rate;
    this.noise(g * 0.5, 0.055, 'bandpass', 800 * p, 3600 * p, 3.2);
    this.noise(g * 0.65, 0.085, 'bandpass', 3800 * p, 900 * p, 3.6, 0.045);
  }

  // 소한공 — 손바람 "훅" 하고 한기가 날아가며 얼음 방울이 튄다
  private palm(g: number, p: number): void {
    this.noise(g, 0.12, 'bandpass', 700 * p, 2400 * p, 1.6);
    this.noise(g * 0.5, 0.16, 'highpass', 5200, 3200, 0.8, 0.03);
    [2637, 3520].forEach((f, i) => this.tone(g * 0.35, f * p, 0.08, 'sine', undefined, 0.04 + i * 0.03));
  }

  // 회광반조 — 받은 "휘-익" 세 가지를 차례로 (못 받았으면 대검 소리로)
  private ashN = 0;
  private ashSwing(g: number, rate: number): void {
    const name = (['ash1', 'ash2', 'ash3'] as const)[this.ashN++ % 3];
    if (!this.sample(name, g, rate * this.rand(0.97, 1.03))) this.greatsword(g * 0.8, rate);
  }

  // 대검 — 같은 검 소리를 낮게 늘여 무게를 싣고 바닥을 울린다 (예전 회광반조. 지금은 ashSwing 이 받은 소리를 못 읽었을 때만)
  private greatsword(g: number, rate: number): void {
    if (!this.sample('sword', g, 0.74 * rate)) this.noise(g * 0.6, 0.2, 'bandpass', 500, 1800, 1.4);
    this.tone(g * 0.55, 95, 0.26, 'sine', 42);
  }

  // 서리 대검 — 차가운 바람이 스치고 얼음이 쨍
  private frostBlade(g: number, p: number): void {
    this.noise(g * 0.8, 0.12, 'bandpass', 2200 * p, 5200 * p, 2.2);
    this.tone(g * 0.5, 2093 * p, 0.2, 'triangle', undefined, 0.05);
    this.tone(g * 0.25, 3136 * p, 0.14, 'sine', undefined, 0.08);
  }

  // 화염 채찍 — 휘익 올라가다 끝에서 "딱"
  private whip(g: number, p: number): void {
    this.noise(g * 0.7, 0.11, 'bandpass', 500 * p, 2600 * p, 1.6);
    this.noise(g * 1.1, 0.02, 'highpass', 3200, 2500, 0.8, 0.1);
    this.tone(g * 0.35, 1500 * p, 0.025, 'square', 600, 0.1);
  }

  // 화염의 랜스 — "쉭" 찌르고 불길이 뒤따른다
  private thrust(g: number, p: number): void {
    this.noise(g * 0.8, 0.07, 'bandpass', 900 * p, 3000 * p, 3);
    this.noise(g * 0.7, 0.2, 'bandpass', 1600 * p, 380, 0.8, 0.03);
    this.tone(g * 0.4, 120 * p, 0.12, 'sine', 60);
  }

  // 전격 질주 — 위로 긁어 올리는 "지잇"
  private dash(g: number, p: number): void {
    this.tone(g * 0.6, 320 * p, 0.08, 'sawtooth', 2600 * p);
    this.crackle(g * 0.5, 0.09, 4, 3000, 7000);
  }

  // 광풍 — 세 번 엇갈려 도는 바람
  private whirl(g: number, p: number): void {
    this.noise(g * 0.8, 0.12, 'bandpass', 500 * p, 2000 * p, 1.6);
    this.noise(g * 0.7, 0.12, 'bandpass', 2200 * p, 700 * p, 1.6, 0.05);
    this.noise(g * 0.6, 0.12, 'bandpass', 800 * p, 2600 * p, 1.6, 0.1);
  }

  // 불 — "화르륵". 훅 하는 바람 + 낮은 몸통 + 타닥거리는 불티.
  private flame(g: number, p: number): void {
    this.noise(g, 0.16, 'bandpass', 1900 * p, 380 * p, 1.1);
    this.tone(g * 0.45, 135 * p, 0.12, 'sine', 62 * p);
    this.crackle(g * 0.2, 0.12, 3, 2200, 4200, 0.02);
  }

  // 얼음 — "쨍". 맑은 두 음 + 잘게 부서지는 결정.
  private iceShot(g: number, p: number): void {
    this.tone(g * 0.8, 1760 * p, 0.1, 'triangle', 1320 * p);
    this.tone(g * 0.4, 2640 * p, 0.07, 'sine', undefined, 0.015);
    this.noise(g * 0.2, 0.02, 'highpass', 5200, 7000, 0.8, 0.01);
  }

  // 테이저 — 짧고 높은 "피융"
  private zap(g: number, p: number): void {
    this.tone(g * 0.7, 2600 * p, 0.06, 'square', 700 * p);
    this.noise(g * 0.5, 0.02, 'bandpass', 5200, 2500, 2);
  }

  // 벼락 — 찢어지는 소리 뒤로 낮게 우르릉
  private thunder(g: number, p: number): void {
    // 받은 전기·지진 소리로 만든 "콰르릉" (tools/build_audio.py). 못 받았으면 합성으로.
    if (this.sample('thunder', g, 0.97 + (p - 1) * 0.5)) return;
    this.crackle(g * 0.7, 0.08, 6, 2500, 7000);
    this.noise(g, 0.45, 'lowpass', 1300 * p, 110, 0.7, 0.03);
    this.tone(g * 0.45, 72 * p, 0.35, 'sawtooth', 40, 0.02);
  }

  // 압축탄 — "퐁"
  private pop(g: number, p: number): void {
    this.tone(g, 440 * p, 0.08, 'sine', 140 * p);
    this.noise(g * 0.5, 0.05, 'bandpass', 1300 * p, 500, 1);
  }

  // 암석 파편 — 짧은 바람 + 돌이 부딪는 "딱"
  private stoneThrow(g: number, p: number): void {
    this.noise(g * 0.6, 0.06, 'bandpass', 700 * p, 1500 * p, 2);
    this.tone(g * 0.45, 190 * p, 0.035, 'square', 120);
  }

  // 파이어볼 — 낮게 "후웅"
  private fireball(g: number, p: number): void {
    this.noise(g, 0.3, 'bandpass', 950 * p, 240, 0.9);
    this.tone(g * 0.5, 92 * p, 0.26, 'sine', 54);
    this.crackle(g * 0.25, 0.2, 3, 2000, 4200, 0.04);
  }

  // 파도 — 물이 밀려가는 "쏴아" + 꿀렁
  private splash(g: number, p: number): void {
    this.noise(g * 0.9, 0.18, 'bandpass', 700 * p, 1700 * p, 1.4);
    this.noise(g * 0.45, 0.12, 'bandpass', 2400 * p, 1100 * p, 3, 0.05);
    this.tone(g * 0.3, 330 * p, 0.1, 'sine', 170);
  }

  // 지진 파동 — 땅이 울리고 자갈이 구른다
  private quake(g: number, p: number, level: number): void {
    // 받은 지진 소리. 파동이 커질수록(레벨) 조금 낮고 크게.
    const big = Math.min(1, (level - 1) / 5);
    if (this.sample('quake', g * (0.8 + 0.35 * big), (1.02 - 0.12 * big) * (0.98 + (p - 1) * 0.3))) return;
    this.tone(g, 64 * p, 0.36, 'sine', 38);
    this.noise(g * 0.8, 0.34, 'lowpass', 420 * p, 110, 0.8);
    this.crackle(g * 0.35, 0.28, 5, 400, 1300, 0.04);
  }

  // 캐스팅 — 아주 작은 종소리 (자주 나가므로 작게)
  private chime(g: number, p: number): void {
    this.tone(g, 1320 * p, 0.13, 'sine');
    this.tone(g * 0.45, 1980 * p, 0.09, 'sine', undefined, 0.012);
  }

  // 여제의 명령 — 벼락 + 위엄 있는 화음
  private royal(g: number, p: number): void {
    // 지지직 모였다가 0.22초에 "콰!" — 그 순간 번개가 떨어진다 (weapons.ts STRIKE_DELAY)
    if (this.sample('royal', g, 1)) return;
    this.thunder(g * 0.9, p * 0.9);
    [523.25, 659.25, 783.99].forEach((f, i) => this.tone(g * 0.22, f * p, 0.45, 'triangle', undefined, 0.02 + i * 0.03));
  }

  // 가스 압착 — "치익" 새는 소리 + 퍽
  private gasBurst(g: number, p: number): void {
    // 받은 불 마법 소리
    if (this.sample('fire', g, 0.96 + (p - 1) * 0.6)) return;
    this.noise(g, 0.18, 'highpass', 3200 * p, 1400, 0.8);
    this.tone(g * 0.5, 150 * p, 0.1, 'sine', 70);
  }

  // 역풍 — 크게 밀어내는 바람
  private windBlast(g: number, p: number): void {
    this.noise(g, 0.28, 'bandpass', 300 * p, 1500 * p, 1);
    this.noise(g * 0.6, 0.2, 'bandpass', 1500 * p, 500 * p, 1.2, 0.08);
  }

  // 기사 — 칼날이 "쉬잉" 울린다
  private steel(g: number, p: number): void {
    this.metal(g * 0.7, 1750 * p, 0.3);
    this.noise(g * 0.6, 0.08, 'bandpass', 4200 * p, 2000 * p, 3);
  }

  // 전류 — "치지직". 짧은 잡음 알갱이를 불규칙하게 흩뿌린다.
  /**
   * 전류. 받은 전기 소리를 네 길이로 잘라 두고, 이번에 옮겨붙은 수만큼 길고 큰 것을 튼다.
   *   1~3 → 0.34초 / 4~6 → 0.44초 / 7~9 → 0.58초 / 10 이상 → 0.69초
   */
  private arc(g: number, hops: number, rate = 1): void {
    const tier = hops <= 3 ? 'elec1' : hops <= 6 ? 'elec2' : hops <= 9 ? 'elec3' : 'elec4';
    const loud = 0.7 + 0.3 * Math.min(1, (hops - 1) / 9);
    // rate 가 1보다 작으면 낮고 길게 — 굵은 번개("굵은 한 줄기")가 이걸 쓴다
    if (this.sample(tier, g * loud, this.rand(0.96, 1.04) * rate)) return;
    this.noise(g * 0.8, 0.02, 'bandpass', 5200, 2600, 2.4);
    this.tone(g * 0.5, this.rand(1900, 2600), 0.035, 'square', 1100);
    this.crackle(g * 0.45, 0.1, 4 + Math.floor(Math.random() * 3), 2600, 6200, 0.012);
  }

  // 거대한 검 — "스윽" / "슥": 검 소리를 낮게 늘여 무게를 싣고 바닥을 쓴다
  private bladeSlash(g: number, p: number): void {
    if (!this.sample('sword', g, 0.66 * p)) this.noise(g, 0.3, 'bandpass', 350 * p, 1400 * p, 1.2);
    this.noise(g * 0.5, 0.28, 'bandpass', 300 * p, 1100 * p, 1.1, 0, 0.03);
  }

  // 거대한 검 회전 — "휭": 바람이 올라갔다 내려온다
  private bladeSpin(g: number, p: number): void {
    this.noise(g, 0.1, 'bandpass', 500 * p, 1900 * p, 1.6);
    this.noise(g * 0.8, 0.12, 'bandpass', 1900 * p, 450 * p, 1.6, 0.08);
  }

  // 감전 — 전류보다 높고 잘게 떠는 정전기 "지지직"
  private staticZap(g: number, p: number): void {
    for (let i = 0; i < 6; i++) {
      this.tone(g * this.rand(0.3, 0.6), this.rand(3000, 6000) * p, 0.012, 'square', undefined, i * this.rand(0.012, 0.02));
    }
    this.noise(g * 0.35, 0.1, 'bandpass', 7000, 6000, 6);
  }

  // 눈바람 — 부드러운 바람 + 방울 몇 개
  private frostField(g: number, p: number): void {
    this.noise(g * 0.7, 0.3, 'bandpass', 1200 * p, 2600 * p, 0.9, 0, 0.05);
    for (let i = 0; i < 3; i++) this.tone(g * 0.25, this.rand(2600, 4200), 0.05, 'sine', undefined, 0.05 + i * 0.06);
  }

  // 돌기둥 — 우르릉 솟다가 "쿵"
  private pillar(g: number, p: number): void {
    this.noise(g * 0.8, 0.2, 'lowpass', 280 * p, 900 * p, 1);
    this.tone(g * 0.7, 115 * p, 0.12, 'square', 70, 0.15);
    this.crackle(g * 0.3, 0.1, 3, 600, 1500, 0.15);
  }

  // 풍화설영 — 눈보라가 휘몰아친다: 바람이 크게 쓸고, 낮게 부풀고, 얼음 방울이 흩어진다
  private blizzard(g: number, p: number): void {
    this.noise(g, 0.7, 'bandpass', 480 * p, 2500 * p, 0.8, 0, 0.12);
    this.noise(g * 0.55, 0.55, 'highpass', 5200, 3000, 0.7, 0.12, 0.1);
    this.tone(g * 0.35, 92 * p, 0.6, 'sine', 55);
    for (let i = 0; i < 6; i++) this.tone(g * 0.22, this.rand(2400, 4400) * p, 0.09, 'sine', undefined, 0.06 + i * 0.07);
  }

  // 얼음이 산산조각 — 높은 유리 파편 소리 몇 알 + 짧은 쨍
  private shatter(g: number, p: number): void {
    this.crackle(g * 0.8, 0.09, 6, 4000 * p, 9000 * p);
    this.metal(g * 0.45, 2600 * p, 0.12);
  }

  // 메자이 황금 광선 — 위에서 내리꽂히는 "쩡" + 낮은 울림
  private beam(g: number, p: number): void {
    this.tone(g * 0.7, 1400 * p, 0.3, 'sine', 700);
    this.metal(g * 0.5, 880 * p, 0.4);
    this.tone(g * 0.6, 110, 0.35, 'sine', 70);
  }

  // 세포 역설 보호막이 깨질 때 — 물컹하게 부풀었다 터진다
  private cellBurst(g: number, p: number): void {
    this.tone(g, 160 * p, 0.12, 'sine', 420);
    this.noise(g * 0.8, 0.35, 'lowpass', 2400 * p, 200, 1.2, 0.08);
    this.tone(g * 0.7, 90, 0.4, 'sine', 45, 0.08);
  }

  // 조합하는 순간 — 위로 번지는 화음 + 반짝이는 고음
  private fusion(g: number): void {
    [261.63, 329.63, 392, 523.25, 659.25, 783.99].forEach((f, i) => this.tone(g * 0.55, f, 0.9, 'triangle', undefined, i * 0.05));
    this.tone(g * 0.5, 1046.5, 1.1, 'sine', undefined, 0.3);
    this.noise(g * 0.25, 0.9, 'highpass', 5000, 9000, 0.7, 0.2, 0.3);
  }

  // 엘리트 출현 — 낮게 으르렁
  private elite(g: number): void {
    this.tone(g, 110, 0.7, 'sawtooth', 70);
    this.tone(g * 0.5, 165, 0.6, 'square', 98, 0.05);
    this.noise(g * 0.3, 0.5, 'lowpass', 600, 150, 0.8);
  }

  // 플레임 스로우가 맞은 자리 — 작게 "퍽" 하고 불티가 튄다
  private emberBurst(g: number, p: number): void {
    this.noise(g, 0.1, 'lowpass', 1800 * p, 260, 0.9);
    this.tone(g * 0.5, 130 * p, 0.08, 'sine', 60);
    this.crackle(g * 0.35, 0.08, 3, 1800, 3800, 0.01);
  }

  // 풍화설영(예전) — 얼음 방울 세 음
  private snowflake(g: number, p: number): void {
    [2093, 2637, 3136].forEach((f, i) => this.tone(g * 0.6, f * p, 0.12, 'sine', undefined, i * 0.04));
    this.noise(g * 0.2, 0.05, 'highpass', 6000, 7000, 0.8);
  }

  // 메자이 — 낮은 징
  private sigil(g: number, p: number): void {
    this.tone(g, 110 * p, 0.9, 'sine');
    this.tone(g * 0.4, 221 * p, 0.7, 'sine');
    this.tone(g * 0.2, 331 * p, 0.5, 'triangle');
  }

  // ── 맞을 때 ────────────────────────────────────────────

  // 베임 — "서걱"
  private hitSlash(g: number, p: number): void {
    this.noise(g, 0.035, 'highpass', 2600 * p, 1200, 0.8);
    this.noise(g * 0.6, 0.05, 'bandpass', 900 * p, 380, 1);
  }

  private hitFire(g: number, p: number): void {
    this.noise(g, 0.07, 'bandpass', 1400 * p, 500, 0.9);
    this.crackle(g * 0.3, 0.06, 2, 2000, 4000);
  }

  // 얼음이 깨지는 "짝"
  private hitIce(g: number, p: number): void {
    this.noise(g * 0.7, 0.025, 'highpass', 4200, 3000, 1);
    this.tone(g * 0.5, 2800 * p, 0.04, 'triangle', 2200 * p);
  }

  private hitShock(g: number, p: number): void {
    this.noise(g, 0.02, 'bandpass', 4500 * p, 2000, 3);
    this.tone(g * 0.4, 1500 * p, 0.025, 'square', 900);
  }

  private hitRock(g: number, p: number): void {
    this.tone(g, 150 * p, 0.07, 'square', 70);
    this.noise(g * 0.6, 0.05, 'lowpass', 900, 200, 0.9);
  }

  private hitWater(g: number, p: number): void {
    this.noise(g, 0.06, 'bandpass', 1100 * p, 600, 2);
    this.tone(g * 0.4, 520 * p, 0.05, 'sine', 260);
  }

  private boomFire(g: number, p: number): void {
    this.tone(g, 92 * p, 0.36, 'sine', 40);
    this.noise(g * 0.9, 0.4, 'lowpass', 1700 * p, 140, 0.7);
    this.crackle(g * 0.3, 0.3, 5, 1800, 4200, 0.03);
  }

  private boomAir(g: number, p: number): void {
    this.tone(g, 170 * p, 0.18, 'sine', 60);
    this.noise(g * 0.7, 0.2, 'bandpass', 1500 * p, 300, 0.8);
  }

  // 사냥 준비 착지 — 묵직한 "쾅"
  private boom(g: number, p: number): void {
    this.tone(g, 80 * p, 0.42, 'sine', 36);
    this.noise(g, 0.3, 'lowpass', 2200 * p, 150, 0.7);
    this.noise(g * 0.5, 0.05, 'highpass', 3000, 2000, 0.7);
  }

  // ── 그 밖 ──────────────────────────────────────────────

  // 처치: 짧은 "퍽". 여러 마리가 죽어도 뭉쳐서 한 번만 난다.
  private kill(g: number): void {
    this.noise(g, 0.055, 'lowpass', this.rand(900, 1300), 260, 0.9);
  }

  // 보석: 주울수록 반음씩 올라가고, 잠깐 쉬면 도로 내려온다
  private gem(g: number): void {
    const now = this.ctx!.currentTime;
    if (now - this.gemLast > 1.1) this.gemStep = 0;
    else this.gemStep = Math.min(11, this.gemStep + 1);
    this.gemLast = now;
    this.tone(g, 880 * Math.pow(2, this.gemStep / 12), 0.06, 'sine');
  }

  // 빙결: 수정 깨지는 듯한 두 음
  private freeze(g: number): void {
    this.tone(g, 2093, 0.14, 'triangle');
    this.tone(g * 0.6, 3136, 0.1, 'triangle', undefined, 0.045);
  }

  // 피격: 낮게 쿵
  private hurt(g: number): void {
    this.tone(g, 180, 0.2, 'sawtooth', 70);
    this.noise(g * 0.5, 0.11, 'lowpass', 700, 180, 0.8);
  }

  // 보스: 낮게 깔리는 뿔나팔
  private boss(g: number): void {
    this.tone(g, 82.4, 1.1, 'sawtooth', 61.7);
    this.tone(g * 0.6, 123.5, 1.0, 'triangle', 92.5, 0.06);
  }

  // 사망: 힘 빠지며 내려가는 음
  private dead(g: number): void {
    this.tone(g, 440, 0.85, 'triangle', 110);
    this.tone(g * 0.5, 220, 0.9, 'sine', 55, 0.05);
  }
}

export const audio = new Audio();
